import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Video, Download } from "lucide-react";
import { http, API } from "@/lib/api";
import { Card } from "@/components/portal/ui";
import { formatIn } from "@/lib/classTime";

function Week({ w, tz, attended }) {
  const past = w.meeting_at && new Date(w.meeting_at) < new Date();
  return (
    <div className="py-3 border-t border-[#E5E1D8]" data-testid={`plan-week-${w.week}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-medium text-[#1A1918] text-sm">Week {w.week}: {w.theme}</div>
        {w.meeting_at && <div className={`text-xs ${past ? "text-[#A79F93]" : "text-[#9F634E]"}`}>Meeting {w.week} · {formatIn(w.meeting_at, tz)} · 30 min</div>}
      </div>
      <div className="text-sm text-[#6B665E] mt-1">{w.lessons.join(" · ")}</div>
      <div className="text-xs text-[#8A998C] mt-1">{w.preparation}</div>
      {w.case_study && <div className="text-xs text-[#8A998C] mt-1">{w.case_study}</div>}
      {past && <div className="text-xs mt-1" data-testid={`attendance-${w.week}`}>{attended === true ? "Attended" : attended === false ? "Missed: you can keep studying; nothing is blocked" : "Attendance not yet recorded"}</div>}
    </div>
  );
}

function FacultyCard({ row }) {
  const { cohort: c, meeting_link, plan, course_slug, attendance } = row;
  const next = c.session_times.find((t) => new Date(t) > new Date());
  const status = c.status === "canceled" ? "Canceled by the Education Center" : c.status === "completed" || !next ? "Meetings completed" : "Enrolled";
  return (
    <Card className="p-6 sm:p-8 mb-6" data-testid={`enrolled-course-${course_slug}`}>
      <div className="eyebrow text-[#9F634E] mb-1" data-testid="faculty-status">Faculty-Led · {status}</div>
      <p className="font-display text-xl text-[#1A1918]">{c.course_code} {c.course_title}</p>
      <p className="text-sm text-[#6B665E] mt-1 flex items-center gap-2"><CalendarClock size={15} /> {c.name} · four weekly 30-minute meetings with {c.instructor} · times in {c.timezone}</p>
      {next && c.status !== "canceled" && <p className="text-sm text-[#1A1918] mt-2" data-testid="faculty-next-meeting">Next meeting: <strong>{formatIn(next, c.timezone)}</strong></p>}
      <div className="flex flex-wrap gap-4 mt-3 text-sm">
        <Link to={`/portal/resources?course=${course_slug}`} className="text-[#9F634E] hover:underline" data-testid="faculty-continue">Continue my lessons</Link>
        {meeting_link
          ? <a href={meeting_link} target="_blank" rel="noopener noreferrer" data-testid="enrolled-meeting-link" className="inline-flex items-center gap-1.5 text-[#9F634E] hover:underline"><Video size={15} /> Join meeting (Zoom / Teams)</a>
          : c.status !== "canceled" && <span className="text-xs text-[#A79F93]" data-testid="enrolled-meeting-pending">The meeting link will appear here before your first meeting.</span>}
        {c.status !== "canceled" && <a href={`${API}/portal/courses/cohorts/${c.id}/calendar.ics`} className="inline-flex items-center gap-1.5 text-[#9F634E] hover:underline" data-testid="faculty-calendar"><Download size={15} /> Download calendar file</a>}
      </div>
      <p className="text-xs text-[#8A998C] mt-3">Your full course is open now. Study ahead, catch up or review at any pace; the meetings never lock or unlock lessons.</p>
      <div className="mt-4" data-testid="enrolled-study-plan">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#A79F93] mb-1">Weekly discussion plan</div>
        {plan.weeks.map((w, i) => <Week key={w.week} w={w} tz={c.timezone} attended={attendance[i]} />)}
        {plan.handouts.length > 0 && <div className="text-xs text-[#8A998C] pt-3 border-t border-[#E5E1D8]">Preparation materials (in your course): {plan.handouts.join(" · ")}</div>}
      </div>
    </Card>
  );
}

function PendingCard({ row }) {
  return (
    <Card className="p-6 sm:p-8 mb-6" data-testid={`enrolled-course-${row.course_slug}`}>
      <div className="eyebrow text-[#9F634E] mb-1" data-testid="faculty-status">Faculty Meetings — Scheduling Pending</div>
      <p className="font-display text-xl text-[#1A1918]">{row.course_code} {row.course_title}</p>
      <p className="text-sm text-[#6B665E] mt-2" data-testid="faculty-pending-note">Andrea Hurley will personally contact you to arrange your four weekly 30-minute online meetings. Once they're confirmed, the dates, time zone, meeting link and reminders appear here.</p>
      {row.calendly_url && <a href={row.calendly_url} target="_blank" rel="noopener noreferrer" data-testid="faculty-calendly" className="inline-flex items-center gap-2 mt-4 bg-[#172A3A] text-[#F5F2E9] rounded-full px-5 py-2.5 text-sm hover:bg-[#22394D] transition-colors"><CalendarClock size={15} /> Choose My Meeting Times</a>}
      {row.calendly_url && <p className="text-xs text-[#8A998C] mt-2">Book your first meeting on Andrea's scheduling page; she confirms all four weekly meetings with you.</p>}
      <Link to={`/portal/resources?course=${row.course_slug}`} className="block mt-3 text-sm text-[#9F634E] hover:underline" data-testid="faculty-continue">Your full course is open now — continue my lessons</Link>
    </Card>
  );
}

export function EnrolledCourses() {
  const [rows, setRows] = useState(null);
  useEffect(() => { http.get("/portal/courses/enrollments").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => {
    if (rows?.length && window.location.hash === "#upcoming-classes") document.getElementById("upcoming-classes")?.scrollIntoView({ behavior: "smooth" });
  }, [rows]);
  if (!rows) return null;
  if (!rows.length) return (
    <Card className="p-5 mb-6 text-sm text-[#4F5B67]" data-testid="no-scheduled-classes">
      <div className="eyebrow text-[#8C7444] mb-1">Faculty-Led Meetings</div>
      You are not enrolled in Faculty-Led Study. Every course can be studied independently at any time; Faculty-Led enrollment is available on each <Link to="/courses" className="text-[#7A6438] underline underline-offset-4">course page</Link>.
    </Card>
  );
  return (
    <section id="upcoming-classes" className="scroll-mt-20" data-testid="upcoming-classes">
      <h2 className="font-academic font-semibold text-2xl text-[#172A3A] mb-3">Faculty-Led Meetings</h2>
      {rows.map((row) => (row.pending ? <PendingCard key={`p-${row.course_slug}`} row={row} /> : <FacultyCard key={row.cohort.id} row={row} />))}
    </section>
  );
}
